import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { Collection } from '@sorare/core/src/atoms/icons/Collection';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { BodyS, LabelL } from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { Progress as CompleteCollectionsCountTaskProgressBar } from '@sorare/core/src/components/rivals/branded/Progress';
import { isType } from '@sorare/core/src/lib/gql';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { CollectionTaskFooter } from '#src/components/missions/CollectionTaskFooter/index.tsx';
import { DumbTask } from '#src/components/missions/DumbTask/index.tsx';
import { Colors } from '#src/components/missions/getTaskColors.tsx';

import { CollectionCompletionDetailsDialog } from './CollectionCompletionDetailsDialog/index.tsx';
import type { CollectionCompletionTask_taskInterface } from './__generated__/index.graphql.ts';

const Img = styled.img`
  width: var(--triple-unit);
  @media ${laptopAndAbove} {
    width: var(--quadruple-unit);
  }
`;
const Footer = styled.div`
  flex: 1;
`;

type Props = {
  collectionCompletionTask: CollectionCompletionTask_taskInterface;
  featured?: boolean;
};
export const CollectionCompletionTask = ({
  collectionCompletionTask,
  featured,
}: Props) => {
  if (!isType(collectionCompletionTask, 'CompleteCollectionsCountTask')) {
    return null;
  }

  const {
    templateIconUrl,
    iconUrl,
    title,
    description,
    liveScore,
    thresholds,
    color,
  } = collectionCompletionTask;

  const currentThresholdIndex = thresholds.findIndex(
    ({ current }) => !!current
  );

  return (
    <DumbTask
      featured={featured}
      color={liveScore > 0 ? Colors.YELLOW : undefined}
      borderColor={liveScore > 0 ? color : undefined}
      icon={!!templateIconUrl && <Img src={templateIconUrl} alt="" />}
      title={
        <Horizontal>
          <LabelL bold>{title}</LabelL>
          {!!iconUrl && <img width={20} src={iconUrl} alt="" />}
        </Horizontal>
      }
      description={
        !!(liveScore > 0 || featured) && (
          <BodyS color="var(--c-nd-600)">
            <Ellipsis visibleLines={2}>{description}</Ellipsis>
          </BodyS>
        )
      }
      detailsDialog={({ open, onClose }) => (
        <CollectionCompletionDetailsDialog
          open={open}
          onClose={onClose}
          task={collectionCompletionTask}
        />
      )}
      footer={
        <Footer>
          <CollectionTaskFooter
            collectionTask={collectionCompletionTask}
            targetIcon={<Collection />}
          >
            <CompleteCollectionsCountTaskProgressBar
              progress={liveScore}
              target={thresholds[currentThresholdIndex]?.count}
              color="var(--c-nd-150-opaque)"
              activeColor="var(--c-yellow-500)"
              lastActiveColor="linear-gradient(to right, var(--c-yellow-500), var(--c-yellow-300))"
              showLabel={false}
            />
          </CollectionTaskFooter>
        </Footer>
      }
    />
  );
};

CollectionCompletionTask.fragments = {
  taskInterface: gql`
    fragment CollectionCompletionTask_taskInterface on TaskInterface {
      id
      iconUrl
      templateIconUrl
      title
      description
      color
      ... on CompleteCollectionsCountTask {
        id
        liveScore
        thresholds {
          count
          current
        }
      }
      ...CollectionTaskFooter_taskInterface
      ...CollectionCompletionDetailsDialog_taskInterface
    }
    ${CollectionTaskFooter.fragments.taskInterface}
    ${CollectionCompletionDetailsDialog.fragments.taskInterface}
  ` as TypedDocumentNode<CollectionCompletionTask_taskInterface>,
};
